import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { fetchProducts } from "../lib/api";
import { chf } from "../lib/format";
import { useCartStore } from "../store/cartStore";
import { logActivity } from "../lib/telemetry";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [product, setProduct] = useState(loc.state?.product || null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(!loc.state?.product);
  const addItem = useCartStore((s) => s.addItem);
  const handle = loc.state?.handle;

  useEffect(() => {
    if (product) return;
    const decoded = decodeURIComponent(id);
    fetchProducts()
      .then((all) => setProduct(all.find((p) => p.id === decoded) || null))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id, product]);

  useEffect(() => { if (product?.id) logActivity("product_view", { product_id: product.id }); }, [product?.id]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-10"><div className="h-96 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} /></div>;
  if (!product) return (
    <div className="max-w-4xl mx-auto px-4 py-20 text-center" style={{ color: "#64748b" }}>
      <p className="font-semibold mb-4">Produit introuvable.</p>
      <button onClick={() => nav("/boutique")} className="font-bold" style={{ color: "#f97316" }}>← Retour à la boutique</button>
    </div>
  );

  const add = () => {
    addItem({ id: product.id, title: product.title, price: product.price, image_url: product.image_url, collectionKeywords: handle, quantity: qty });
    logActivity("cart_add", { product_id: product.id, meta: { qty } });
    toast.success("Ajouté au panier");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 mb-4 font-semibold" style={{ color: "#0b1220" }} data-testid="product-back">
        <ArrowLeft size={20} /> Retour
      </button>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="rounded-2xl aspect-square flex items-center justify-center" style={{ background: "#f8fafc", border: "1px solid #f0f0f0" }}>
          {product.image_url && <img src={product.image_url} alt={product.title} className="w-full h-full object-contain p-6" />}
        </div>
        <div>
          <h1 className="text-2xl font-extrabold" style={{ color: "#0b1220" }}>{product.title}</h1>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {product.promo_active && product.original_price > product.price && (
              <span className="text-xl line-through" style={{ color: "#9aa0a6" }}>{chf(product.original_price)}</span>
            )}
            <p className="text-3xl font-extrabold" style={{ color: "#f97316" }}>{chf(product.price)}</p>
            {product.promo_active && product.promo_label && (
              <span data-testid="product-promo-badge" className="text-xs font-extrabold px-2 py-1 rounded-full text-white" style={{ background: "#f97316" }}>{product.promo_label}</span>
            )}
          </div>
          <p className="text-sm mt-5 whitespace-pre-line leading-relaxed" style={{ color: "#475569" }}>{product.description}</p>

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ border: "1px solid #e5e7eb" }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus" style={{ color: "#0b1220" }}><Minus size={18} /></button>
              <span className="font-bold w-6 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-plus" style={{ color: "#0b1220" }}><Plus size={18} /></button>
            </div>
            <button onClick={add} data-testid="product-add-to-cart"
              className="flex-1 rounded-xl font-bold text-white py-3 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
              <ShoppingCart size={20} /> Ajouter au panier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
